import { DynamicLink } from './dynamic-link';
import { IconKey } from './icon';
import { BlockWrapperInterface, FlexibleBlock } from './portal-block';
import { PortalBlockData } from './portal-block-data';
import { FlexibleBlockHooks } from './portal-block-flexible-hooks';
import { FlexibleBlockBrickIntegration } from './portal-block-flexible-integration';
import { PortalStyleConfig } from './portal-theme';
import { TailwindClassName } from './tailwind-class-name';
import { ThemeButtonColorConfig, ThemeColorConfig } from './theme';
import { ThemeColorSchemas } from './theme-color-schemas';

export type FlexibleBlockBrickType =
  | 'meta'
  | 'progress'
  | 'timeline'
  | 'rating'
  | 'carousel'
  | 'image'
  | 'avatar'
  | 'countdown'
  | 'text'
  | 'title'
  | 'button'
  | 'search'
  | 'social'
  | 'link'
  | 'code'
  | 'tiles'
  | 'submenu'
  | 'process-state'
  | 'process-action'
  | 'form'
  | 'banner'
  | 'integration'
  | 'diagram'
  | 'ai-agent';

export type FlexibleBlockTemplateType =
  | ('submenu' | 'process-state' | 'process-action')
  | ('image' | 'custom' | 'code')
  | ('timeline' | 'carousel' | 'countdown' | 'info-card' | 'tiles')
  | ('hero-event' | 'hero-impression' | 'hero-search' | 'hero-headline' | 'hero-counsalting')
  | ('limit' | 'form' | 'banner' | 'title' | 'text')
  | ('integration' | 'diagram')
  | 'ai-agent';

interface FlexibleBlockTemplateBase<
  T extends FlexibleBlockTemplateType,
  V extends string = FlexibleBlockTemplateViewBasic
> {
  type: T;
  view: V;
}

type FlexibleBlockTemplateViewBasic = 'basic';
export type FlexibleBlockTemplateSubmenu = FlexibleBlockTemplateBase<'submenu', 'basic'>;
export type FlexibleBlockTemplateProcessState = FlexibleBlockTemplateBase<'process-state'>;
export type FlexibleBlockTemplateProcessAction = FlexibleBlockTemplateBase<'process-action'>;
export type FlexibleBlockTemplateCustom = FlexibleBlockTemplateBase<'custom'>;
export type FlexibleBlockTemplateCode = FlexibleBlockTemplateBase<'code'>;
export type FlexibleBlockTemplateImage = FlexibleBlockTemplateBase<'image'>;
export type FlexibleBlockTemplateTimelineView = 'horizontal' | 'vertical';
export type FlexibleBlockTemplateTimeline = FlexibleBlockTemplateBase<'timeline', FlexibleBlockTemplateTimelineView>;
export type FlexibleBlockTemplateTiles = FlexibleBlockTemplateBase<'tiles'>;
export type FlexibleBlockTemplateCarousel = FlexibleBlockTemplateBase<'carousel'>;
export type FlexibleBlockTemplateCountdown = FlexibleBlockTemplateBase<'countdown'>;
export type FlexibleBlockTemplateInfoCardView = 'top-image' | 'left-image' | 'right-image' | 'fill-image' | 'compact';
export type FlexibleBlockTemplateInfoCard = FlexibleBlockTemplateBase<'info-card', FlexibleBlockTemplateInfoCardView>;
export type FlexibleBlockTemplateHeroEvent = FlexibleBlockTemplateBase<'hero-event'>;
export type FlexibleBlockTemplateHeroImpression = FlexibleBlockTemplateBase<'hero-impression'>;
export type FlexibleBlockTemplateHeroSearch = FlexibleBlockTemplateBase<'hero-search'>;
export type FlexibleBlockTemplateHeroHeadline = FlexibleBlockTemplateBase<'hero-headline'>;
export type FlexibleBlockTemplateHeroCounsalting = FlexibleBlockTemplateBase<'hero-counsalting'>;
export type FlexibleBlockTemplateLimit = FlexibleBlockTemplateBase<'limit'>;
export type FlexibleBlockTemplateForm = FlexibleBlockTemplateBase<'form'>;
export type FlexibleBlockTemplateBanner = FlexibleBlockTemplateBase<'banner'>;
export type FlexibleBlockTemplateTitle = FlexibleBlockTemplateBase<'title'>;
export type FlexibleBlockTemplateText = FlexibleBlockTemplateBase<'text'>;
export type FlexibleBlockTemplateIntegration = FlexibleBlockTemplateBase<'integration'>;
export type FlexibleBlockTemplateDiagram = FlexibleBlockTemplateBase<'diagram'>;
export type FlexibleBlockTemplateAiAgent = FlexibleBlockTemplateBase<'ai-agent'>;

export type FlexibleBlockTemplate =
  | FlexibleBlockTemplateSubmenu
  | FlexibleBlockTemplateCustom
  | FlexibleBlockTemplateCode
  | FlexibleBlockTemplateImage
  | FlexibleBlockTemplateTimeline
  | FlexibleBlockTemplateCarousel
  | FlexibleBlockTemplateCountdown
  | FlexibleBlockTemplateInfoCard
  | FlexibleBlockTemplateHeroEvent
  | FlexibleBlockTemplateHeroImpression
  | FlexibleBlockTemplateHeroSearch
  | FlexibleBlockTemplateHeroHeadline
  | FlexibleBlockTemplateHeroCounsalting
  | FlexibleBlockTemplateTiles
  | FlexibleBlockTemplateProcessState
  | FlexibleBlockTemplateProcessAction
  | FlexibleBlockTemplateLimit
  | FlexibleBlockTemplateForm
  | FlexibleBlockTemplateBanner
  | FlexibleBlockTemplateTitle
  | FlexibleBlockTemplateText
  | FlexibleBlockTemplateIntegration
  | FlexibleBlockTemplateDiagram
  | FlexibleBlockTemplateAiAgent;

export interface FlexibleBlockBrickBase {
  id?: string;
  index: number;
  brickType: FlexibleBlockBrickType;
  brickName: string;
  enabled: boolean;
  switchable: boolean;
  removable?: boolean;
  sortable?: boolean;
  className?: TailwindClassName[];
  theme?: ThemeColorConfig;
  mainBrick?: boolean;
}

interface BrickRelatedMenuItem {
  relatedMenuItemId: string;
}

export interface FlexibleBlockBrickMeta extends FlexibleBlockBrickBase {
  brickType: 'meta';
  dynamicLink?: DynamicLink;
}

export interface FlexibleBlockBrickSubmenuItem {
  name: string;
  url: string;
  icon: string;
  visible: boolean;
  className?: string[];
}

export interface FlexibleBlockBrickSubmenu extends FlexibleBlockBrickBase, BrickRelatedMenuItem {
  brickType: 'submenu';
  items: FlexibleBlockBrickSubmenuItem[];
  showAddItem: boolean;
  addItemTitle?: string;
  addItemPrefix?: string;
  onAddItem?: () => void;
}

interface BrickInnerButtonBase<State extends string = 'active'> {
  name: string;
  state: {
    [key in State]: {
      text: string;
      icon?: IconKey;
    };
  };
  theme: ThemeButtonColorConfig;
}

export interface FlexibleBlockBrickProcessState extends FlexibleBlockBrickBase, BrickRelatedMenuItem {
  brickType: 'process-state';
  theme: ThemeColorConfig<'surface' | 'completed', 'main', never>;
}

export interface FlexibleBlockBrickProcessAction extends FlexibleBlockBrickBase, BrickRelatedMenuItem {
  brickType: 'process-action';
  buttons: {
    [key in 'start' | 'continue' | 'incomplete' | 'return']: Omit<BrickInnerButtonBase, 'theme'>;
  } & {
    [key in 'complete' | 'finish']: Omit<BrickInnerButtonBase<'active' | 'done'>, 'theme'>;
  } & {
    [key in 'navigation']: Omit<BrickInnerButtonBase<'prev' | 'next'>, 'theme'>;
  };
  theme: ThemeButtonColorConfig;
}

export interface FlexibleBlockBrickProgress extends FlexibleBlockBrickBase {
  brickType: 'progress';
  value: number;
  limit: number;
  unit: string;
  colored: boolean;
  showButton: boolean;
  buttonText: string;
}

export enum FlexibleBlockBrickTimelineStyle {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export interface FlexibleBlockBrickTimelineItem {
  title: string;
  done: boolean;
  dynamicLink?: DynamicLink;
}

export interface FlexibleBlockBrickTimeline extends FlexibleBlockBrickBase {
  brickType: 'timeline';
  style: FlexibleBlockBrickTimelineStyle;
  items: FlexibleBlockBrickTimelineItem[];
}

export enum FlexibleBlockBrickRatingStyle {
  STAR = 'star',
  HEART = 'heart',
}

export interface FlexibleBlockBrickRating extends FlexibleBlockBrickBase {
  brickType: 'rating';
  style: FlexibleBlockBrickRatingStyle;
  value: number;
  colored: boolean;
}

export enum FlexibleBlockBrickCarouselStyle {
  BASIC = 'basic',
}

export interface FlexibleBlockBrickCarouselItem {
  index: number;
  src: string;
  type: 'video' | 'image';
  caption?: string;
  description?: string;
  dynamicLink?: DynamicLink;
  button?: {
    text: string;
    dynamicLink?: DynamicLink;
  };
}

export interface FlexibleBlockBrickCarousel extends FlexibleBlockBrickBase {
  brickType: 'carousel';
  showControls: boolean;
  showIndicators: boolean;
  style: FlexibleBlockBrickCarouselStyle;
  items: FlexibleBlockBrickCarouselItem[];
}

export interface FlexibleBlockBrickImage extends FlexibleBlockBrickBase {
  brickType: 'image';
  src?: string;
}

export enum FlexibleBlockBrickAvatarStyle {
  RING = 'ring',
  SQUARE = 'square',
}

export interface FlexibleBlockBrickAvatar extends FlexibleBlockBrickBase {
  brickType: 'avatar';
  src?: string;
  name: string;
  description: string;
  style: FlexibleBlockBrickAvatarStyle;
}

export enum FlexibleBlockBrickCountdownStyle {
  LABELS = 'labels',
  BOXES = 'boxes',
}

export interface FlexibleBlockBrickCountdown extends FlexibleBlockBrickBase {
  brickType: 'countdown';
  timestamp: number;
  title: string;
  style: FlexibleBlockBrickCountdownStyle;
}

export interface FlexibleBlockBrickTitle extends FlexibleBlockBrickBase {
  brickType: 'title';
  text: string;
  placeholder: string;
}

export interface FlexibleBlockBrickText extends FlexibleBlockBrickBase {
  brickType: 'text';
  text: string;
  placeholder: string;
}

export interface FlexibleBlockBrickLink extends FlexibleBlockBrickBase {
  brickType: 'link';
  dynamicLink: DynamicLink;
}

export enum FlexibleBlockBrickButtonStyle {
  BASIC = 'basic',
  IMPRESSION = 'impression',
}

export interface FlexibleBlockBrickButton extends FlexibleBlockBrickBase {
  brickType: 'button';
  text: string;
  noLink?: boolean;
  dynamicLink?: DynamicLink;
  style?: FlexibleBlockBrickButtonStyle;
}

export interface FlexibleBlockBrickSearch extends FlexibleBlockBrickBase {
  brickType: 'search';
  placeholder: string;
  target: '_self' | '_blank';
}

export interface FlexibleBlockBrickSocialItem {
  show: boolean;
  title: string;
  icon: string;
  dynamicLink: DynamicLink;
}
export interface FlexibleBlockBrickSocial extends FlexibleBlockBrickBase {
  brickType: 'social';
  items: FlexibleBlockBrickSocialItem[];
}

export interface FlexibleBlockBrickCode extends FlexibleBlockBrickBase {
  brickType: 'code';
  html: string;
  style: string;
  script: string;
}

export interface FlexibleBlockBrickTileItem {
  index: number;
  title: string;
  image: string;
  dynamicLink?: DynamicLink;
  theme?: ThemeColorConfig;
}

export interface FlexibleBlockBrickTiles extends FlexibleBlockBrickBase {
  brickType: 'tiles';
  items: FlexibleBlockBrickTileItem[];
}

export type BrickFormItemType = 'text' | 'email' | 'phone' | 'number' | 'textarea' | 'checkbox';

export interface BrickFormItem {
  id: string;
  type: BrickFormItemType;
  title: string;
  required: boolean;
  cols: number;
  placeholder?: string;
}
export interface BrickFormSubmit {
  show: boolean;
  text: string;
}

export interface FlexibleBlockBrickForm extends FlexibleBlockBrickBase {
  brickType: 'form';
  submit: BrickFormSubmit;
  items: BrickFormItem[];
  reusable: boolean;
}

export interface FlexibleBlockBrickBanner extends FlexibleBlockBrickBase {
  brickType: 'banner';
  title: string;
  bgImage: string;
  description: string;
  dynamicLink: DynamicLink;
  buttonText: string;
}

export interface FlexibleBlockBrickDiagramBase extends FlexibleBlockBrickBase {
  brickType: 'diagram';
  diagramType: 'bpmn' | 'reactflow';
  height: number;
}

export interface FlexibleBlockBrickDiagramBpmn extends FlexibleBlockBrickDiagramBase {
  diagramType: 'bpmn';
}

export interface FlexibleBlockBrickDiagramReactflow extends FlexibleBlockBrickDiagramBase {
  diagramType: 'reactflow';
}

export type FlexibleBlockBrickDiagram = FlexibleBlockBrickDiagramBpmn | FlexibleBlockBrickDiagramReactflow;

export interface FlexibleBlockBrickAiAgent extends FlexibleBlockBrickBase {
  brickType: 'ai-agent';
  agentId: string | null;
  allowUserInputAfterResult: boolean;
}

export type FlexibleBlockBrick =
  | FlexibleBlockBrickMeta
  | FlexibleBlockBrickSubmenu
  | FlexibleBlockBrickProgress
  | FlexibleBlockBrickTimeline
  | FlexibleBlockBrickRating
  | FlexibleBlockBrickCarousel
  | FlexibleBlockBrickImage
  | FlexibleBlockBrickAvatar
  | FlexibleBlockBrickCountdown
  | FlexibleBlockBrickTitle
  | FlexibleBlockBrickText
  | FlexibleBlockBrickLink
  | FlexibleBlockBrickButton
  | FlexibleBlockBrickSearch
  | FlexibleBlockBrickSocial
  | FlexibleBlockBrickCode
  | FlexibleBlockBrickTiles
  | FlexibleBlockBrickProcessState
  | FlexibleBlockBrickProcessAction
  | FlexibleBlockBrickForm
  | FlexibleBlockBrickBanner
  | FlexibleBlockBrickRating
  | FlexibleBlockBrickIntegration
  | FlexibleBlockBrickDiagram
  | FlexibleBlockBrickAiAgent;

export interface BrickUpdateProps<T extends FlexibleBlockBrick> {
  index: number;
  brick: T;
  squashId?: string;
}
export interface BrickRemoveProps {
  index: number;
  squashId?: string;
}
export interface BrickReorderProps {
  oldIndex: number;
  newIndex: number;
  squashId?: string;
}
export interface BrickAddProps {
  brick: FlexibleBlockBrick;
  squashId?: string;
}

export type BrickBlurProps<T extends FlexibleBlockBrick> = BrickUpdateProps<T>;

export interface BrickBaseProps<T extends FlexibleBlockBrick> {
  brick: T;
  blockId: string;
  brickIndex: number;
  blockData: PortalBlockData;
  themeStyle: PortalStyleConfig;
  themeColors: ThemeColorSchemas;
  onBrickBlur: (props: BrickBlurProps<T>) => void;
  onBrickRemove: (props: BrickRemoveProps) => void;
  onBrickReorder: (props: BrickReorderProps) => void;
  onBrickUpdate: (props: BrickUpdateProps<T>) => void;
}

export interface FlexibleBlockProps<T extends FlexibleBlockTemplate = FlexibleBlockTemplate> {
  template: T;
  title: string;
  bricks: FlexibleBlockBrick[];
  wrapper: BlockWrapperInterface;
  className?: TailwindClassName[];
}

export interface FlexBlockBaseProps {
  sectionRoot: string;
  block: FlexibleBlock;
  themeStyle: PortalStyleConfig;
  themeColors: ThemeColorSchemas;
  hooks: FlexibleBlockHooks;
}

export interface BrickBaseComponentProps {
  id: string;
  brick: FlexibleBlockBrick;
  sectionRoot: string;
  block: FlexibleBlock;
  hooks: FlexibleBlockHooks;
  themeStyle: PortalStyleConfig;
  themeColors: ThemeColorSchemas;
}
